/**
 * ValidationScoreSummary Component
 *
 * A high-end, modern dashboard card displaying key validation scoring metrics.
 * The Overall Score is the prominent focal point using a large circular gauge.
 * Uses MUI Card elevations, consistent spacing, shadows, and transitions.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/AssessmentOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import {
  IValidationScore,
  IValidationScoreBreakdown,
} from "../../types/Scoring.type";
import {
  getValidationExecutionHistory,
  recalculateValidationScore,
} from "../../services/scoringService";
import { validationScoringConfigDefaults } from "../../services/constants";
import { useToast } from "../../services/utils";
import ScoreBadge from "./ScoreBadge";
import ScoreBreakdownDialog from "./ScoreBreakdownDialog";
import ScoringOverrideDialog from "./ScoringOverrideDialog";
import {
  getScoreColor,
  getScoreBg,
  getScoreLabel,
  LOW_BAR_GRADIENT,
  MED_BAR_GRADIENT,
  HIGH_BAR_GRADIENT,
  LOW_CHIP_BG,
  LOW_CHIP_COLOR,
  MED_CHIP_BG,
  MED_CHIP_COLOR,
  HIGH_CHIP_BG,
  HIGH_CHIP_COLOR,
  METRIC_ACCENT,
  LOW_IMPACT_COLOR,
  HIGH_IMPACT_COLOR,
} from "./scoreTheme";

/** Number of history rows fetched per request (first record becomes the Latest Score card) */
const HISTORY_PAGE_SIZE = 25;

/** Format snake_case key to Title Case label */
const formatConfigKey = (key: string): string =>
  key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/** Inline table rendering a config_snapshot object */
const ConfigSnapshotTable: React.FC<{
  snapshot: any;
  calculatedOn?: string;
}> = ({ snapshot, calculatedOn }) => {
  if (!snapshot || typeof snapshot !== "object") {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
        No configuration snapshot available.
      </Typography>
    );
  }
  const entries = Object.entries(snapshot).filter(
    ([, v]) => v !== null && v !== undefined && typeof v !== "object",
  );
  return (
    <Box sx={{ p: 1.5 }}>
      {calculatedOn && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          Calculated on:{" "}
          <strong>{new Date(calculatedOn).toLocaleString()}</strong>
        </Typography>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                Parameter
              </TableCell>
              <TableCell
                sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                align="right"
              >
                Value
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(([key, value]) => (
              <TableRow
                key={key}
                sx={{ "&:last-child td": { borderBottom: 0 } }}
              >
                <TableCell sx={{ fontSize: "0.8rem" }}>
                  {formatConfigKey(key)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontSize: "0.8rem", fontFamily: "monospace" }}
                >
                  {String(value)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

interface IValidationScoreSummaryProps {
  executionId: number | null | undefined;
}

/** Circular gauge that renders the Overall Score as the visual focal point. */
const ScoreGauge: React.FC<{ score: number }> = ({ score }) => {
  const color = getScoreColor(score);
  const percent = score * 100;
  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Background ring */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={120}
        thickness={5}
        sx={{ color: "action.disabledBackground", position: "absolute" }}
      />
      {/* Foreground ring */}
      <CircularProgress
        variant="determinate"
        value={percent}
        size={120}
        thickness={5}
        sx={{
          color,
          transition: "all 0.6s cubic-bezier(.4,0,.2,1)",
          "& .MuiCircularProgress-circle": {
            strokeLinecap: "round",
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{ color, lineHeight: 1.1, letterSpacing: "-0.02em" }}
        >
          {percent.toFixed(1)}%
        </Typography>
        <Chip
          label={getScoreLabel(score)}
          size="small"
          sx={{
            mt: 0.5,
            fontWeight: 700,
            fontSize: "0.65rem",
            height: 18,
            backgroundColor: color,
            color: "#fff",
          }}
        />
      </Box>
    </Box>
  );
};

/** Small metric tile used for secondary KPIs. */
const MetricTile: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  color?: string;
  icon: React.ReactNode;
}> = ({ title, value, subtitle, color = "#1976d2", icon }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      backgroundColor: "background.paper",
      transition: "box-shadow 0.25s ease, transform 0.25s ease",
      "&:hover": { boxShadow: 3, transform: "translateY(-2px)" },
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
      <Box sx={{ color, display: "flex", opacity: 0.85 }}>{icon}</Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: "0.65rem",
        }}
      >
        {title}
      </Typography>
    </Box>
    <Typography variant="h6" fontWeight={700} sx={{ color, lineHeight: 1.2 }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

/** Stacked severity bar with chip legend. */
const ImpactBar: React.FC<{
  label: string;
  low: number;
  medium: number;
  high: number;
  total: number;
}> = ({ label, low, medium, high, total }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {(total * 100).toFixed(2)}%
      </Typography>
    </Box>
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        height: 10,
        borderRadius: 1.5,
        overflow: "hidden",
        backgroundColor: "action.disabledBackground",
      }}
    >
      {low > 0 && (
        <Tooltip title={`Low: ${(low * 100).toFixed(2)}%`} arrow>
          <Box
            sx={{
              flex: low,
              background: LOW_BAR_GRADIENT,
              borderRadius: 0.5,
              transition: "flex 0.4s ease",
            }}
          />
        </Tooltip>
      )}
      {medium > 0 && (
        <Tooltip title={`Medium: ${(medium * 100).toFixed(2)}%`} arrow>
          <Box
            sx={{
              flex: medium,
              background: MED_BAR_GRADIENT,
              borderRadius: 0.5,
              transition: "flex 0.4s ease",
            }}
          />
        </Tooltip>
      )}
      {high > 0 && (
        <Tooltip title={`High: ${(high * 100).toFixed(2)}%`} arrow>
          <Box
            sx={{
              flex: high,
              background: HIGH_BAR_GRADIENT,
              borderRadius: 0.5,
              transition: "flex 0.4s ease",
            }}
          />
        </Tooltip>
      )}
    </Box>
    <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
      <Chip
        label={`Low: ${(low * 100).toFixed(2)}%`}
        size="small"
        sx={{
          fontSize: "0.68rem",
          height: 20,
          backgroundColor: LOW_CHIP_BG,
          color: LOW_CHIP_COLOR,
          fontWeight: 600,
        }}
      />
      <Chip
        label={`Med: ${(medium * 100).toFixed(2)}%`}
        size="small"
        sx={{
          fontSize: "0.68rem",
          height: 20,
          backgroundColor: MED_CHIP_BG,
          color: MED_CHIP_COLOR,
          fontWeight: 600,
        }}
      />
      <Chip
        label={`High: ${(high * 100).toFixed(2)}%`}
        size="small"
        sx={{
          fontSize: "0.68rem",
          height: 20,
          backgroundColor: HIGH_CHIP_BG,
          color: HIGH_CHIP_COLOR,
          fontWeight: 600,
        }}
      />
    </Box>
  </Box>
);

const ValidationScoreSummary: React.FC<IValidationScoreSummaryProps> = ({
  executionId,
}) => {
  const [scoreData, setScoreData] = useState<IValidationScore | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedHistoryScore, setSelectedHistoryScore] =
    useState<IValidationScore | null>(null);
  const [recalculating, setRecalculating] = useState(false);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const { showToast } = useToast();

  // Score history state — shared by the main card and the history accordion.
  // On page 0 the first item becomes the Latest Score focal point; the rest
  // populate the history table. On subsequent pages only the history table
  // is refreshed — the Latest Score card stays pinned to page 1 / items[0].
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historyData, setHistoryData] = useState<IValidationScore[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryConfigs, setExpandedHistoryConfigs] = useState<
    Set<number>
  >(new Set());

  const toggleHistoryConfig = (id: number) => {
    setExpandedHistoryConfigs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Load page 1 and promote items[0] to the Latest Score card. */
  const loadInitial = useCallback(async () => {
    if (!executionId) return;
    setLoading(true);
    setError(false);
    try {
      const res = await getValidationExecutionHistory({
        executionId,
        page: 1,
        pSize: HISTORY_PAGE_SIZE,
      });
      const items: IValidationScore[] = res?.items || [];
      setScoreData(items[0] || null);
      setHistoryData(items);
      setHistoryTotal(res?.total || 0);
      setHistoryPage(0);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  const fetchHistoryPage = useCallback(
    async (uiPage: number) => {
      if (!executionId) return;
      setHistoryLoading(true);
      try {
        const res = await getValidationExecutionHistory({
          executionId,
          page: uiPage + 1, // API is 1-based
          pSize: HISTORY_PAGE_SIZE,
        });
        if (res) {
          setHistoryData(res.items || []);
          setHistoryTotal(res.total || 0);
        }
      } catch {
        // silently ignore
      } finally {
        setHistoryLoading(false);
      }
    },
    [executionId],
  );

  const handleRecalculate = async (configOverride: Record<string, any>) => {
    if (!executionId) return;
    setRecalculating(true);
    try {
      await recalculateValidationScore(executionId, configOverride);
      // After a successful recalculation, re-fetch page 1 so both the Latest
      // Score card and the history table reflect the newly-appended record.
      await loadInitial();
      showToast("Score recalculated successfully", "success");
      setOverrideDialogOpen(false);
    } catch {
      showToast("Failed to recalculate score", "error");
    } finally {
      setRecalculating(false);
    }
  };

  // Initial load — populates main card + first page of history in one call
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadInitial();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadInitial]);

  // Pagination — only re-fetches history (page > 0 skips main-card promotion)
  useEffect(() => {
    if (historyPage > 0) {
      fetchHistoryPage(historyPage);
    }
  }, [historyPage, fetchHistoryPage]);

  // On page 1, items[0] is already the Latest Score card, so hide it from
  // the history table. On subsequent pages, every item is historical.
  const historyTableRows =
    historyPage === 0 ? historyData.slice(1) : historyData;
  const historyCountLabel = Math.max(historyTotal - 1, 0);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !scoreData) {
    return (
      <>
        <Card
          elevation={4}
          sx={{
            borderRadius: 3,
            background: "background.paper",
            mb: 3,
            overflow: "visible",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AssessmentIcon sx={{ color: "text.disabled", fontSize: 22 }} />
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ letterSpacing: "0.01em" }}
                >
                  Data Quality Score
                </Typography>
              </Box>
              <Button
                variant="contained"
                className="btn-orange"
                startIcon={<RefreshIcon />}
                disabled={recalculating || !executionId}
                onClick={() => setOverrideDialogOpen(true)}
              >
                Recalculate Score
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              No score data available.
            </Typography>
          </CardContent>
        </Card>
        <ScoringOverrideDialog
          open={overrideDialogOpen}
          onClose={() => setOverrideDialogOpen(false)}
          type="validation"
          currentConfig={null}
          fallbackDefaults={validationScoringConfigDefaults}
          recalculating={recalculating}
          onRecalculate={handleRecalculate}
        />
      </>
    );
  }

  const details: IValidationScoreBreakdown = scoreData.score_details;
  const scoreColor = getScoreColor(scoreData.score);

  return (
    <>
      <Card
        elevation={2}
        sx={{
          mb: 2,
          borderRadius: 3,
          overflow: "visible",
          transition: "box-shadow 0.3s ease",
          "&:hover": { boxShadow: 6 },
        }}
      >
        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
          {/* Header row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssessmentIcon sx={{ color: scoreColor, fontSize: 22 }} />
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ letterSpacing: "0.01em" }}
              >
                Data Quality Score
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="contained"
                className="btn-orange"
                startIcon={<RefreshIcon />}
                disabled={recalculating || !executionId}
                onClick={() => setOverrideDialogOpen(true)}
              >
                Recalculate Score
              </Button>
              <Tooltip title="View full score breakdown" arrow>
                <IconButton
                  size="small"
                  onClick={() => setDialogOpen(true)}
                  sx={{
                    transition: "background-color 0.2s",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <OpenInFullIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Main content: Gauge focal point + KPI tiles */}
          <Grid container spacing={3} alignItems="center">
            {/* Focal point: Score Gauge */}
            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 1,
                  px: 2,
                  borderRadius: 3,
                  background: getScoreBg(scoreData.score),
                  transition: "transform 0.3s ease",
                  "&:hover": { transform: "scale(1.02)" },
                }}
              >
                <ScoreGauge score={scoreData.score} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, fontWeight: 500 }}
                >
                  Calculated{" "}
                  {new Date(scoreData.calculated_on).toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>

            {/* Secondary KPI tiles */}
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <MetricTile
                    title="Total Records"
                    value={details.total_records.toLocaleString()}
                    subtitle={`Impact per record: ${details.each_records_impact.toFixed(6)}`}
                    color={METRIC_ACCENT}
                    icon={<TrendingUpIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MetricTile
                    title="Record Level Impact"
                    value={`${(details.total_record_impact * 100).toFixed(2)}%`}
                    subtitle="Deduction from record-level issues"
                    color={
                      details.total_record_impact > 0.1
                        ? HIGH_IMPACT_COLOR
                        : LOW_IMPACT_COLOR
                    }
                    icon={<WarningAmberIcon fontSize="small" />}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MetricTile
                    title="Issue Level Impact"
                    value={`${(details.total_issue_impact * 100).toFixed(2)}%`}
                    subtitle="Deduction from issue-level severity"
                    color={
                      details.total_issue_impact > 0.1
                        ? HIGH_IMPACT_COLOR
                        : LOW_IMPACT_COLOR
                    }
                    icon={<InfoOutlinedIcon fontSize="small" />}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          {/* Impact Breakdown section */}
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ mb: 1.5, letterSpacing: "0.02em" }}
          >
            Impact Breakdown
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ImpactBar
                label="Record Impact"
                low={details.record_impact_low}
                medium={details.record_impact_medium}
                high={details.record_impact_high}
                total={details.total_record_impact}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ImpactBar
                label="Issue Impact"
                low={details.issue_impact_low}
                medium={details.issue_impact_medium}
                high={details.issue_impact_high}
                total={details.total_issue_impact}
              />
            </Grid>
          </Grid>

          {/* Scoring Config Used */}
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              mt: 2,
              "&:before": { display: "none" },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 40,
                "& .MuiAccordionSummary-content": { my: 0.5 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TuneIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Scoring Config Used
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <ConfigSnapshotTable
                snapshot={scoreData.config_snapshot}
                calculatedOn={scoreData.calculated_on}
              />
            </AccordionDetails>
          </Accordion>

          {/* Score History */}
          <Accordion
            expanded={historyExpanded}
            onChange={(_, expanded) => setHistoryExpanded(expanded)}
            disableGutters
            elevation={0}
            sx={{
              mt: 2,
              "&:before": { display: "none" },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 40,
                "& .MuiAccordionSummary-content": { my: 0.5 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <HistoryIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Score History
                </Typography>
                {historyCountLabel > 0 && (
                  <Chip
                    label={historyCountLabel}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                    }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {historyLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 3,
                  }}
                >
                  <CircularProgress size={22} />
                </Box>
              ) : historyTableRows.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: "center" }}
                >
                  No historical scores available.
                </Typography>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                          >
                            Date
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                            align="right"
                          >
                            Score
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              width: 40,
                            }}
                            align="center"
                          >
                            Config
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historyTableRows.map((entry) => (
                          <React.Fragment key={entry.id}>
                            <TableRow
                              sx={{
                                "&:last-child td": {
                                  borderBottom: expandedHistoryConfigs.has(
                                    entry.id,
                                  )
                                    ? undefined
                                    : 0,
                                },
                                backgroundColor:
                                  entry.id === scoreData.id
                                    ? "action.selected"
                                    : "transparent",
                              }}
                            >
                              <TableCell sx={{ fontSize: "0.8rem" }}>
                                {new Date(
                                  entry.calculated_on,
                                ).toLocaleDateString()}{" "}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {new Date(
                                    entry.calculated_on,
                                  ).toLocaleTimeString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <ScoreBadge
                                  score={entry.score}
                                  size="small"
                                  onClick={() => {
                                    setSelectedHistoryScore(entry);
                                    setDialogOpen(true);
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center" sx={{ p: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleHistoryConfig(entry.id)}
                                  sx={{
                                    transition: "transform 0.2s",
                                    transform: expandedHistoryConfigs.has(
                                      entry.id,
                                    )
                                      ? "rotate(180deg)"
                                      : "none",
                                  }}
                                >
                                  <ExpandMoreIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            {expandedHistoryConfigs.has(entry.id) && (
                              <TableRow>
                                <TableCell
                                  colSpan={3}
                                  sx={{ p: 0, backgroundColor: "action.hover" }}
                                >
                                  <ConfigSnapshotTable
                                    snapshot={entry.config_snapshot}
                                    calculatedOn={entry.calculated_on}
                                  />
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {historyTotal > HISTORY_PAGE_SIZE && (
                    <TablePagination
                      component="div"
                      count={historyTotal}
                      page={historyPage}
                      onPageChange={(_, newPage) => setHistoryPage(newPage)}
                      rowsPerPage={HISTORY_PAGE_SIZE}
                      rowsPerPageOptions={[HISTORY_PAGE_SIZE]}
                      sx={{
                        borderTop: "1px solid",
                        borderColor: "divider",
                        "& .MuiTablePagination-toolbar": {
                          minHeight: 36,
                        },
                      }}
                    />
                  )}
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      <ScoreBreakdownDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedHistoryScore(null);
        }}
        type="validation"
        scoreData={selectedHistoryScore ?? scoreData}
      />

      <ScoringOverrideDialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        type="validation"
        currentConfig={scoreData.config_snapshot}
        fallbackDefaults={validationScoringConfigDefaults}
        recalculating={recalculating}
        onRecalculate={handleRecalculate}
      />
    </>
  );
};

export default ValidationScoreSummary;
